// File: src/pages/Procurement/VendorSettlementPage.jsx

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import VendorPaymentModal from '@/components/Procurement/VendorPaymentModal';

const VendorSettlementPage = () => {
  const { role } = useUserPermissions();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const fetchPayments = async () => {
    try {
      const res = await axios.get('/solo-mdsh-api/vendor-payments');
      setPayments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleNewPayment = () => {
    fetchPayments(); // Reload to include latest entry
    setShowModal(false);
  };

  if (role !== 'admin') {
    return (
      <div className="p-6 text-red-400">
        <h2 className="text-xl font-bold mb-2">🚫 Restricted Access</h2>
        <p>You do not have permission to manage vendor payments.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 bg-background text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">💰 Vendor Settlement Tracker</h1>
        <Button onClick={() => setShowModal(true)} className="bg-green-600 hover:bg-green-700">
          <Plus size={16} className="mr-2" /> Add Payment
        </Button>
      </div>

      {loading ? (
        <div className="text-blue-300 flex items-center gap-2">
          <Loader2 className="animate-spin" /> Loading payments...
        </div>
      ) : payments.length === 0 ? (
        <p className="text-yellow-400">No vendor payments found.</p>
      ) : (
        <div className="grid gap-4">
          {payments.map((pmt, idx) => (
            <Card key={idx} className="bg-neutral-900 border border-white/10 p-4 rounded-xl">
              <div className="flex justify-between">
                <div>
                  <p className="text-white font-bold">{pmt.vendor_name || 'Unknown Vendor'}</p>
                  <p className="text-sm text-gray-400">Amount: £{parseFloat(pmt.amount_paid).toFixed(2)}</p>
                  <p className="text-sm text-gray-400">Method: {pmt.payment_method || 'N/A'}</p>
                  {pmt.notes && <p className="text-sm text-yellow-300 italic mt-1">Note: {pmt.notes}</p>}
                </div>
                <div className="text-right text-sm text-gray-500">
                  <p>Paid by: {pmt.paid_by || 'System'}</p>
                  <p>{new Date(pmt.payment_date).toLocaleDateString()}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <VendorPaymentModal
          open={showModal}
          onClose={() => setShowModal(false)}
          onSaved={handleNewPayment}
        />
      )}
    </div>
  );
};

export default VendorSettlementPage;
